import {
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Box,
    Divider,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AvailableDrugs from '../transaction/components/AvailableDrugs.tsx';
import { useEffect, useState } from 'react';
import StorageService from './services/StorageService.ts';
import { SaldoThresholds } from '../../application/types/saldoThresholds.ts';
import { useGlobalStore } from '../../application/store/store.tsx';
import { StorageLocationType } from '../../application/types/storageLocation.ts';

const EditStorage = ({ storageData, setStorageData }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDrug, setSelectedDrug] = useState(null);
    const [maxValue, setMaxValue] = useState('');
    const [restockThreshold, setRestockThreshold] = useState('');
    const [loadingUpdate, setLoadingUpdate] = useState(false);
    const [loadingSave, setLoadingSave] = useState(false);
    const [loadingAdd, setLoadingAdd] = useState(false);
    const [thresholdsDirty, setThresholdsDirty] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState({
        open: false,
        index: null as number | null,
    });
    const setMessage = useGlobalStore((state) => state.setMessage);
    const storageService = new StorageService();

    const [storageName, setStorageName] = useState(storageData.name);
    const [storageLocation, setStorageLocation] = useState(
        storageData.location
    );
    const [storageDescription, setStorageDescription] = useState(
        storageData.description
    );
    const [storageDigitalId] = useState(storageData.digitalId);

    // Used to set DigitalId which is currently not in use.
    // const [storageDigitalId, setStorageDigitalId] = useState(
    //     storageData.digitalId
    // );
    const [satelliteStorage, setSatelliteStorage] = useState(
        storageData?.isSatelliteStorage ?? false
    );
    const [storageBarCode, setStorageBarCode] = useState(storageData.barCode);
    const [storageType, setStorageType] = useState(
        storageData.type ?? StorageLocationType.StorageCabinet
    );

    useEffect(() => {
        if (storageData?.isSatelliteStorage !== undefined) {
            setSatelliteStorage(storageData.isSatelliteStorage);
        }
        if (storageData?.type !== undefined) {
            setStorageType(storageData.type);
        }
    }, [storageData]);

    const clearFields = () => {
        setSelectedDrug(null);
        setSearchTerm('');
        setMaxValue('');
        setRestockThreshold('');
    };

    const deleteThreshold = async (index: number) => {
        const removeItem = storageData.inventory[index].saldoThresholds;
        if (
            removeItem.id === '' ||
            removeItem.id === undefined ||
            removeItem.id === null
        ) {
            setMessage(
                'Kan inte ta bort gränsvärde utan ett ID. Spara och försök igen!',
                'warning'
            );
            return;
        }
        const updatedInventory = storageData.inventory.filter(
            (item) => item.saldoThresholds.id !== removeItem.id
        );
        try {
            await storageService.deleteSaldoThreshold(removeItem.id);
            setStorageData({
                ...storageData,
                inventory: updatedInventory,
            });
            setMessage('Gränsvärde borttaget!', 'success');
        } catch (error) {
            if (
                // a bit redundant after graying out the button but I'll leave it.
                error?.response?.data?.exception === 'UnauthorizedException' ||
                error?.response?.status === 401
            ) {
                setMessage(
                    'Kan inte ta bort gränsvärdet eftersom det finns kvarvarande läkemedel i lagret.',
                    'error'
                );
            } else {
                setMessage(
                    'Ett fel uppstod vid borttagning av gränsvärdet!',
                    'error'
                );
            }
        }
    };

    const onChange = (event) => {
        const { name, value } = event.target;
        const clamped = value === '' ? '' : String(Math.max(0, Number(value)));
        switch (name) {
            case 'max':
                setMaxValue(clamped);
                break;
            case 'restock':
                setRestockThreshold(clamped);
                break;
            default:
                break;
        }
    };

    const updateStorage = async () => {
        setLoadingUpdate(true);
        const updatedStorageData = {
            ...storageData,
            name: storageName,
            location: storageLocation,
            description: storageDescription,
            digitalId: storageDigitalId,
            isSatelliteStorage: satelliteStorage,
            barCode: storageBarCode,
            type: storageType,
        };
        try {
            await storageService.updateStorage(updatedStorageData);
            setStorageData(updatedStorageData);
        } catch (error) {
            setMessage(
                'Ett fel uppstod vid uppdatering av lagerställe!',
                'error'
            );
            console.error('Error updating storage:', error);
        } finally {
            setLoadingUpdate(false);
        }
    };

    const saveNewThresholds = async () => {
        setLoadingSave(true);
        try {
            let updatedThresholdList = [];
            for (const threshold of storageData.inventory) {
                const updatedThresholds: SaldoThresholds = {
                    id: threshold.saldoThresholds.id,
                    maxValue: parseInt(threshold.saldoThresholds.maxValue),
                    restockThreshold: parseInt(
                        threshold.saldoThresholds.restockThreshold
                    ),
                    storageLocationId: storageData.id,
                    drugId: threshold.drug.id,
                };
                updatedThresholdList.push(updatedThresholds);
            }
            await storageService.updateSaldoThresholds(updatedThresholdList);
            setMessage('Gränsvärden sparade!', 'success');
            setThresholdsDirty(false);
        } catch (error) {
            console.error('Error saving thresholds:', error);
            setMessage('Ett fel upstod vid sparning av gränsvärden!', 'error');
        } finally {
            setLoadingSave(false);
        }
    };

    const saveDrugThresholds = async () => {
        const maxValid =
            maxValue !== '' &&
            Number.isFinite(Number(maxValue)) &&
            Number(maxValue) > 0;
        const restockValid =
            restockThreshold !== '' &&
            Number.isFinite(Number(restockThreshold)) &&
            Number(restockThreshold) >= 0 &&
            Number(restockThreshold) < Number(maxValue);

        if (loadingAdd || !selectedDrug || !maxValid || !restockValid) {
            return;
        }
        setLoadingAdd(true);
        try {
            const exists = storageData.inventory?.some(
                (item) => item.drug?.id === selectedDrug.id
            );
            if (exists) {
                setMessage('Läkemedlet finns redan i listan.', 'warning');
                return;
            }
            const newInventoryItem = {
                drug: selectedDrug,
                saldoThresholds: {
                    id: null,
                    drug: selectedDrug,
                    maxValue: maxValue,
                    restockThreshold: restockThreshold,
                    storageLocationId: storageData.id,
                    drugId: selectedDrug.id,
                },
            };
            const newStorageData = {
                ...storageData,
                inventory: [...storageData.inventory, newInventoryItem],
            };
            setStorageData(newStorageData);
            setThresholdsDirty(true);
            setMessage('Läkemedel tillagt!', 'success');

            clearFields();
        } catch (error) {
            setMessage(
                'Ett fel uppstod vid sparning av nya gränsvärden!',
                'error'
            );
            console.error('Error saving saldo threshold:', error);
        } finally {
            setLoadingAdd(false);
        }
    };

    const handleThresholdChange = (index, field, value) => {
        const updatedInventory = [...storageData.inventory];
        const inventoryItem = updatedInventory[index];

        if (!inventoryItem.saldoThresholds) {
            console.error(
                `saldoThresholds is missing for inventory item at index ${index}`
            );
            inventoryItem.saldoThresholds = {};
        }

        inventoryItem.saldoThresholds[field] = value;
        setStorageData({
            ...storageData,
            inventory: updatedInventory,
        });
        setThresholdsDirty(true);
    };

    const openDeleteConfirm = (index: number) => {
        setConfirmDelete({ open: true, index });
    };
    const handleConfirmDelete = async () => {
        if (confirmDelete.index === null) return;
        await deleteThreshold(confirmDelete.index);
        setConfirmDelete({ open: false, index: null });
    };
    const handleCancelDelete = () => {
        setConfirmDelete({ open: false, index: null });
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4">
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Storage Information Card */}
                <Card
                    elevation={0}
                    className="shadow-sm border border-gray-200"
                >
                    <CardContent className="p-6">
                        <Box className="mb-6">
                            <Typography
                                variant="h5"
                                className="font-semibold text-gray-900 mb-2"
                            >
                                Lagerställeinformation
                            </Typography>
                            <Typography
                                variant="body2"
                                className="text-gray-600"
                            >
                                Uppdatera grundläggande information om
                                lagerställe
                            </Typography>
                        </Box>

                        <Stack spacing={4}>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={3}
                            >
                                <TextField
                                    label="Lagerställenamn"
                                    name="name"
                                    value={storageName}
                                    onChange={(e) =>
                                        setStorageName(e.target.value)
                                    }
                                    fullWidth
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&:hover fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        },
                                    }}
                                />
                                <TextField
                                    label="Plats"
                                    name="location"
                                    value={storageLocation}
                                    onChange={(e) =>
                                        setStorageLocation(e.target.value)
                                    }
                                    fullWidth
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&:hover fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        },
                                    }}
                                />
                            </Stack>

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={3}
                            >
                                <TextField
                                    label="Beskrivning"
                                    name="description"
                                    value={storageDescription}
                                    onChange={(e) =>
                                        setStorageDescription(e.target.value)
                                    }
                                    fullWidth
                                    variant="outlined"
                                    multiline
                                    rows={2}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&:hover fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        },
                                    }}
                                />
                                {/* <TextField
                                    label="Digitalt ID"
                                    name="digitalId"
                                    value={storageDigitalId}
                                    onChange={(e) =>
                                        setStorageDigitalId(e.target.value)
                                    }
                                    fullWidth
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&:hover fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        },
                                    }}
                                /> */}
                            </Stack>

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={3}
                            >
                                <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                                    <TextField
                                        label="Streckkod"
                                        name="barCode"
                                        value={storageBarCode || ''}
                                        onChange={(e) =>
                                            setStorageBarCode(e.target.value)
                                        }
                                        fullWidth
                                        variant="outlined"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: 'primary.main',
                                                },
                                            },
                                        }}
                                    />
                                </Box>
                                <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                                    <FormControl fullWidth>
                                        <InputLabel id="type-select-label">
                                            Typ
                                        </InputLabel>
                                        <Select
                                            labelId="type-select-label"
                                            id="type-select"
                                            value={storageType}
                                            label="Typ"
                                            name="type"
                                            onChange={(e) =>
                                                setStorageType(e.target.value)
                                            }
                                        >
                                            <MenuItem
                                                value={
                                                    StorageLocationType.StorageCabinet
                                                }
                                            >
                                                Läkemedelsskåp
                                            </MenuItem>
                                            <MenuItem
                                                value={
                                                    StorageLocationType.OperatingRoom
                                                }
                                            >
                                                Operationssal
                                            </MenuItem>
                                            <MenuItem
                                                value={
                                                    StorageLocationType.AmbulanceBag
                                                }
                                            >
                                                Akutväska
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Stack>

                            <Box className="flex justify-start">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={satelliteStorage}
                                            onChange={(e) =>
                                                setSatelliteStorage(
                                                    e.target.checked
                                                )
                                            }
                                        />
                                    }
                                    label="Akutväska/Satellitlagring"
                                    labelPlacement="end"
                                />
                            </Box>

                            <Divider />

                            <Box className="flex justify-end">
                                <Button
                                    startIcon={
                                        loadingUpdate ? (
                                            <CircularProgress
                                                size={20}
                                                color="inherit"
                                            />
                                        ) : (
                                            <SaveIcon />
                                        )
                                    }
                                    onClick={updateStorage}
                                    variant="contained"
                                    size="large"
                                    disabled={loadingUpdate}
                                    sx={{
                                        minWidth: 200,
                                        py: 1.5,
                                        '&:hover': {
                                            transform: 'none',
                                            boxShadow: 'none',
                                        },
                                    }}
                                >
                                    Uppdatera Lagerställe
                                </Button>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>

                {/* Threshold Management Card */}
                <Card
                    elevation={0}
                    className="shadow-sm border border-gray-200"
                >
                    <CardContent className="p-6">
                        <Box className="mb-6">
                            <Typography
                                variant="h5"
                                className="font-semibold text-gray-900 mb-2"
                            >
                                Gränsvärden för läkemedel
                            </Typography>
                            <Typography
                                variant="body2"
                                className="text-gray-600"
                            >
                                Hantera maximalt lagersaldo och
                                beställningspunkter för läkemedel
                            </Typography>
                        </Box>

                        {/* Add New Threshold Section */}
                        <Box className="mb-6 p-4 bg-gray-50 rounded-lg">
                            <Typography
                                variant="h6"
                                className="font-medium text-gray-900 mb-4"
                            >
                                Lägg till nytt gränsvärde
                            </Typography>
                            <Stack
                                spacing={3}
                                direction={{ xs: 'column', lg: 'row' }}
                                alignItems="flex-end"
                            >
                                <Box className="w-full">
                                    <AvailableDrugs
                                        searchTerm={searchTerm}
                                        setSearchTerm={setSearchTerm}
                                        handleSelect={setSelectedDrug}
                                        filterOut={storageData?.inventory?.map(
                                            (item) => item.drug
                                        )}
                                        allTenantDrugs={true}
                                    />
                                </Box>
                                <TextField
                                    label="Maximalt lagersaldo"
                                    type="number"
                                    name="max"
                                    value={maxValue}
                                    onChange={onChange}
                                    fullWidth
                                    variant="outlined"
                                    inputProps={{ min: 0 }}
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === '-' ||
                                            e.key === '.' ||
                                            e.key.toLowerCase() === 'e'
                                        ) {
                                            e.preventDefault();
                                        }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&:hover fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        },
                                    }}
                                />
                                <TextField
                                    label="Beställningspunkt"
                                    type="number"
                                    name="restock"
                                    value={restockThreshold}
                                    onChange={onChange}
                                    fullWidth
                                    variant="outlined"
                                    inputProps={{ min: 0 }}
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === '-' ||
                                            e.key === '.' ||
                                            e.key.toLowerCase() === 'e'
                                        ) {
                                            e.preventDefault();
                                        }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&:hover fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        },
                                    }}
                                />
                                <Button
                                    startIcon={<AddIcon />}
                                    disabled={
                                        loadingAdd ||
                                        !selectedDrug ||
                                        !(
                                            maxValue !== '' &&
                                            Number(maxValue) > 0
                                        ) ||
                                        !(
                                            restockThreshold !== '' &&
                                            Number(restockThreshold) >= 0 &&
                                            Number(restockThreshold) <
                                                Number(maxValue)
                                        )
                                    }
                                    onClick={saveDrugThresholds}
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        minWidth: 140,
                                        py: 1.5,
                                        '&:hover': {
                                            transform: 'none',
                                            boxShadow: 'none',
                                        },
                                    }}
                                >
                                    Lägg till
                                </Button>
                            </Stack>
                        </Box>
                        {/* Existing Thresholds Table */}
                        <Box className="overflow-x-auto">
                            <Typography
                                variant="h6"
                                className="font-medium text-gray-900 mb-4"
                            >
                                Befintliga gränsvärden
                            </Typography>
                            <TableContainer
                                component={Paper}
                                elevation={0}
                                className="border border-gray-200"
                            >
                                <Table>
                                    <TableHead className="bg-gray-50">
                                        <TableRow>
                                            <TableCell className="font-semibold text-gray-900">
                                                Läkemedel
                                            </TableCell>
                                            <TableCell className="font-semibold text-gray-900">
                                                Maximalt lagersaldo
                                            </TableCell>
                                            <TableCell className="font-semibold text-gray-900">
                                                Beställningspunkt
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                className="font-semibold text-gray-900"
                                            >
                                                Åtgärder
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {storageData.inventory.map(
                                            (inventory, index) => (
                                                <TableRow
                                                    key={`${inventory.drug.id}-${index}`}
                                                    className="hover:bg-gray-50"
                                                >
                                                    <TableCell className="font-medium">
                                                        {`${inventory.drug.drugName} ${inventory.drug.concentration} ${inventory.drug.concentrationUnit}, ${inventory.drug.quantity} ${inventory.drug.quantityUnit}`}
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            type="number"
                                                            value={
                                                                inventory
                                                                    .saldoThresholds
                                                                    ?.maxValue ||
                                                                ''
                                                            }
                                                            inputProps={{
                                                                min: 0,
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (
                                                                    e.key ===
                                                                        '-' ||
                                                                    e.key.toLowerCase() ===
                                                                        'e'
                                                                ) {
                                                                    e.preventDefault();
                                                                }
                                                            }}
                                                            onChange={(e) => {
                                                                const v =
                                                                    e.target
                                                                        .value;
                                                                const clamped =
                                                                    v === ''
                                                                        ? ''
                                                                        : String(
                                                                              Math.max(
                                                                                  0,
                                                                                  Number(
                                                                                      v
                                                                                  )
                                                                              )
                                                                          );
                                                                handleThresholdChange(
                                                                    index,
                                                                    'maxValue',
                                                                    clamped
                                                                );
                                                            }}
                                                            size="small"
                                                            sx={{
                                                                minWidth: 100,
                                                                '& .MuiOutlinedInput-root':
                                                                    {
                                                                        '&:hover fieldset':
                                                                            {
                                                                                borderColor:
                                                                                    'primary.main',
                                                                            },
                                                                    },
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            type="number"
                                                            value={
                                                                inventory
                                                                    .saldoThresholds
                                                                    ?.restockThreshold ||
                                                                ''
                                                            }
                                                            inputProps={{
                                                                min: 0,
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (
                                                                    e.key ===
                                                                        '-' ||
                                                                    e.key.toLowerCase() ===
                                                                        'e'
                                                                ) {
                                                                    e.preventDefault();
                                                                }
                                                            }}
                                                            onChange={(e) => {
                                                                const v =
                                                                    e.target
                                                                        .value;
                                                                const clamped =
                                                                    v === ''
                                                                        ? ''
                                                                        : String(
                                                                              Math.max(
                                                                                  0,
                                                                                  Number(
                                                                                      v
                                                                                  )
                                                                              )
                                                                          );
                                                                handleThresholdChange(
                                                                    index,
                                                                    'restockThreshold',
                                                                    clamped
                                                                );
                                                            }}
                                                            size="small"
                                                            sx={{
                                                                minWidth: 100,
                                                                '& .MuiOutlinedInput-root':
                                                                    {
                                                                        '&:hover fieldset':
                                                                            {
                                                                                borderColor:
                                                                                    'primary.main',
                                                                            },
                                                                    },
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip
                                                            title={
                                                                inventory.quantityRemaining !==
                                                                0
                                                                    ? 'Töm lagret på detta läkemedel innan du kan ta bort gränsvärdet.'
                                                                    : 'Ta bort gränsvärde'
                                                            }
                                                            arrow
                                                        >
                                                            <span>
                                                                <IconButton
                                                                    onClick={() =>
                                                                        openDeleteConfirm(
                                                                            index
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        inventory.quantityRemaining !==
                                                                        0
                                                                    }
                                                                    color="error"
                                                                    size="small"
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box className="flex justify-end mt-4">
                                <Button
                                    startIcon={
                                        loadingSave ? (
                                            <CircularProgress
                                                size={20}
                                                color="inherit"
                                            />
                                        ) : (
                                            <SaveIcon />
                                        )
                                    }
                                    onClick={saveNewThresholds}
                                    variant="contained"
                                    size="large"
                                    disabled={loadingSave || !thresholdsDirty}
                                    sx={{
                                        minWidth: 200,
                                        py: 1.5,
                                        '&:hover': {
                                            transform: 'none',
                                            boxShadow: 'none',
                                        },
                                    }}
                                >
                                    Spara Gränsvärden
                                </Button>
                            </Box>
                            <Dialog
                                open={confirmDelete.open}
                                onClose={handleCancelDelete}
                            >
                                <DialogTitle>Ta bort gränsvärde</DialogTitle>
                                <DialogContent>
                                    Är du säker på att du vill ta bort
                                    gränsvärdet?
                                </DialogContent>
                                <DialogActions>
                                    <Button
                                        onClick={handleCancelDelete}
                                        color="primary"
                                    >
                                        Avbryt
                                    </Button>
                                    <Button
                                        onClick={handleConfirmDelete}
                                        color="error"
                                        variant="contained"
                                    >
                                        Ta bort
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Box>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default EditStorage;
